import { App } from '@capacitor/app';
import { CapacitorHttp } from '@capacitor/core';
import { esApp } from './plataforma';

/** Los dos dominios desde los que un enlace puede abrir la app. */
export interface DominiosDelEntorno {
	/** La web pública: `https://app.arcadigital.app` en PROD. */
	app: string | undefined;
	/** La API: `https://api.arcadigital.app` en PROD. */
	api: string | undefined;
}

export type DestinoDeEnlace =
	| { tipo: 'pantalla'; ruta: string }
	| { tipo: 'enlace-personal'; url: string }
	| { tipo: 'enlace-de-recuperacion'; url: string }
	| null;

/**
 * Las pantallas de entrada: las únicas a las que un enlace de fuera puede llevar.
 *
 * 🔴 Tienen que coincidir con las rutas declaradas en `public/well-known/` (lo que el sistema
 * entrega a la app) y en el manifiesto de Android. Añadir aquí una ruta que el sistema no entrega
 * no hace nada; declarar una que aquí falta abre la app y la deja donde estaba.
 */
export const PANTALLAS_DE_ENTRADA = ['/', '/token', '/accept-user-invitation'];

/**
 * Las secciones de entrada: cualquier pantalla del panel. Es a donde vuelve el KYC («Volver a la
 * plataforma» apunta a la pantalla de la que salió el usuario) y a donde llevan los botones de
 * referidos de los correos. Mismo aviso que arriba: tiene que coincidir con `public/well-known/` y
 * con el manifiesto de Android.
 */
export const SECCIONES_DE_ENTRADA = ['/dashboard/'];

/**
 * Los enlaces del dominio de la API que hay que canjear: el personal (`/m/`) y el de recuperar el
 * acceso (`/r/`). Los declara la API en sus propios `/.well-known/` (repo comparsa) y aquí el
 * manifiesto de Android. `/k/`, que lleva al KYC, NO está: ese es del navegador.
 */
export const ENLACES_DE_LA_API = ['/m/', '/r/'];

/** La pantalla que inicia la sesión: la única en la que puede acabar un canje. */
const PANTALLA_DE_SESION = '/token';

/** Una pantalla del panel: `/dashboard/<pantalla>` y, como mucho, un identificador detrás. */
const PANTALLA_DEL_PANEL = /^\/dashboard\/[\w-]+(\/[\w-]+)?$/;

/**
 * A dónde va un enlace personal que no se ha podido canjear: al panel. Con sesión lo abre; sin
 * ella, la ruta privada manda al login. No a `/` ni a `/login`, que pintan el formulario de acceso
 * aunque haya sesión.
 */
const RUTA_DEL_PANEL = '/dashboard/home';

/** El enlace personal del correo de bienvenida: `/m/<token opaco>`, en el dominio de la API. */
const ENLACE_PERSONAL = /^\/m\/[^/]+$/;

/** El enlace del correo de recuperar el acceso: `/r/<token de un solo uso>`, también en la API. */
const ENLACE_DE_RECUPERACION = /^\/r\/[^/]+$/;

const dominiosDelEntorno = (): DominiosDelEntorno => ({
	app: process.env.REACT_APP_APP_URL,
	api: process.env.REACT_APP_BASE_API
});

const aUrl = (direccion: string | undefined): URL | null => {
	try {
		return new URL(direccion ?? '');
	} catch {
		return null;
	}
};

/**
 * A dónde lleva, dentro de la app, un enlace que llega de fuera. `null` si no es nuestro.
 *
 * Lo que entra aquí viene de un correo, de otra app o de un QR, y termina en una navegación con
 * sesión. Por eso la lista es cerrada: `https`, exactamente nuestro dominio y exactamente una de
 * nuestras rutas de entrada. Todo lo demás se descarta.
 */
export const destinoDeEnlace = (
	enlace: string,
	dominios: DominiosDelEntorno = dominiosDelEntorno()
): DestinoDeEnlace => {
	const url = aUrl(enlace);
	if (url?.protocol !== 'https:') {
		return null;
	}

	if (url.host === aUrl(dominios.app)?.host) {
		// La portada lleva al panel: `/` pinta el formulario de acceso aunque haya sesión.
		if (url.pathname === '/') {
			return { tipo: 'pantalla', ruta: RUTA_DEL_PANEL };
		}

		if (PANTALLAS_DE_ENTRADA.includes(url.pathname) || PANTALLA_DEL_PANEL.test(url.pathname)) {
			return { tipo: 'pantalla', ruta: `${url.pathname}${url.search}` };
		}

		return null;
	}

	if (url.host === aUrl(dominios.api)?.host) {
		// La dirección normalizada, que es la que se ha validado: es la que se pide después por la
		// vía nativa, cuyo analizador de direcciones no es el del navegador.
		if (ENLACE_PERSONAL.test(url.pathname)) {
			return { tipo: 'enlace-personal', url: url.href };
		}

		if (ENLACE_DE_RECUPERACION.test(url.pathname)) {
			return { tipo: 'enlace-de-recuperacion', url: url.href };
		}
	}

	return null;
};

const cabecera = (cabeceras: Record<string, string> | undefined, nombre: string) =>
	Object.entries(cabeceras ?? {}).find(([clave]) => clave.toLowerCase() === nombre)?.[1];

/**
 * La pantalla a la que redirige la respuesta de un canje, si es la que inicia la sesión. Cualquier
 * otra cosa (la portada de un enlace caducado, otro dominio, otra pantalla) no vale: `null`.
 */
const pantallaDeSesion = (
	cabeceras: Record<string, string> | undefined,
	dominios: DominiosDelEntorno
): string | null => {
	const destino = destinoDeEnlace(cabecera(cabeceras, 'location') ?? '', dominios);

	return destino?.tipo === 'pantalla' && destino.ruta.startsWith(`${PANTALLA_DE_SESION}?`)
		? destino.ruta
		: null;
};

/**
 * Convertir el enlace personal del correo en la pantalla de entrada a la que apunta.
 *
 * El enlace (`https://api…/m/<token>`) no es una pantalla: comparsa responde con una redirección a
 * `https://app…/token?token=<sesión>`. En el navegador esa redirección se sigue sola; la app tiene
 * que leerla ella, y desde el WebView no puede (CORS no deja ver una redirección entre dominios).
 * Se pide por la vía nativa, sin seguirla, y se lee su `Location`.
 *
 * Mantenimiento: lo sencillo sería que el correo apuntase al dominio de la app y no al de la API
 * —un solo dominio que declarar y ningún canje—. Se ha hecho así para no tocar la plataforma; ver
 * «Enlaces que abren la app» en README_APP_MOVIL.md.
 */
export const canjearEnlacePersonal = async (
	url: string,
	dominios: DominiosDelEntorno = dominiosDelEntorno()
): Promise<string | null> => {
	try {
		const respuesta = await CapacitorHttp.get({ url, disableRedirects: true });

		return pantallaDeSesion(respuesta.headers, dominios);
	} catch {
		return null;
	}
};

/**
 * Canjear el enlace del correo de recuperar el acceso (`https://api…/r/<token>`).
 *
 * No se canjea leyéndolo: abrirlo solo devuelve una página con un botón, para que los escáneres de
 * correo no lo gasten. Lo que lo canjea —y lo quema: es de un solo uso— es el ENVÍO de ese botón,
 * que responde con la redirección a `/token`. La app lo envía ella directamente: aquí no hay
 * escáner, el enlace lo ha abierto una persona.
 */
export const canjearEnlaceDeRecuperacion = async (
	url: string,
	dominios: DominiosDelEntorno = dominiosDelEntorno()
): Promise<string | null> => {
	try {
		const respuesta = await CapacitorHttp.post({ url, disableRedirects: true });

		return pantallaDeSesion(respuesta.headers, dominios);
	} catch {
		return null;
	}
};

/**
 * Abrir dentro de la app un enlace que llega de fuera. Si no es nuestro, no hace nada.
 *
 * Con recarga completa y no navegando por dentro: la ruta `/token` lee su token una sola vez, al
 * arrancar la aplicación. Y con `replace`, para que el enlace con el token no quede en el
 * historial.
 */
export const abrirEnlace = async (
	enlace: string,
	dominios: DominiosDelEntorno = dominiosDelEntorno()
): Promise<void> => {
	const destino = destinoDeEnlace(enlace, dominios);
	if (!destino) {
		return;
	}

	// Un enlace de canje que ya no vale (caducado, usado, rotado o sin red) lleva al panel: el sistema ya ha
	// abierto la app en vez del navegador, y no hacer nada dejaría al usuario sin saber qué ha pasado.
	const canjear = {
		'enlace-personal': canjearEnlacePersonal,
		'enlace-de-recuperacion': canjearEnlaceDeRecuperacion
	};

	const ruta =
		destino.tipo === 'pantalla'
			? destino.ruta
			: (await canjear[destino.tipo](destino.url, dominios)) ?? RUTA_DEL_PANEL;

	window.location.replace(ruta);
};

/**
 * Escuchar los enlaces con los que el sistema abre la app (Universal Links en iOS, App Links en
 * Android).
 *
 * Capacitor guarda el enlace de un arranque en frío hasta que alguien escucha, así que este único
 * registro cubre los dos casos: la app ya abierta y la app recién lanzada por el enlace.
 *
 * En la web no hace nada: allí esos enlaces ya son la propia web.
 */
export const registrarEnlaces = (): void => {
	if (!esApp()) {
		return;
	}

	void App.addListener('appUrlOpen', ({ url }) => {
		void abrirEnlace(url);
	});
};
